"use client";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Droplets, Snowflake, Zap } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ImmersionCooledSystems() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: card1Ref,
    isVisible: card1Visible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: card2Ref,
    isVisible: card2Visible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: card3Ref,
    isVisible: card3Visible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const systems = [{
    icon: Droplets,
    title: "Immersion Cooling Technology",
    description: "Our advanced immersion cooling systems submerge mining hardware in non-conductive dielectric fluid, enabling optimal thermal management and significantly improved energy efficiency compared to traditional air-cooled systems.",
    linkText: "Learn More",
    linkUrl: "#"
  }, {
    icon: Snowflake,
    title: "Enhanced Performance",
    description: "Immersion cooling eliminates hot spots and enables higher density deployments. By maintaining consistent operating temperatures, we extend hardware lifespan and maximize computational throughput per unit of energy consumed.",
    linkText: "Technical Details",
    linkUrl: "#"
  }, {
    icon: Zap,
    title: "Sustainable Operations",
    description: "Our immersion infrastructure reduces cooling energy requirements by up to 95%, minimizes water usage, and operates with reduced noise levels. This approach aligns with our commitment to environmentally responsible digital asset operations.",
    linkText: "View Specifications",
    linkUrl: "#"
  }];
  return <section id="immersioncooled-systems" className="bg-background py-[var(--spacing-xl)] px-[var(--spacing-md)] md:px-[var(--spacing-lg)]">
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className={`text-center mb-[var(--spacing-xl)] transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
          <h2 className="text-foreground mb-[var(--spacing-sm)]">
            Immersion-Cooled Systems
          </h2>
          <p className="text-muted-foreground max-w-3xl mx-auto leading-[1.6]">
            West Indian Digital deploys state-of-the-art immersion cooling infrastructure for maximum efficiency and reduced environmental impact.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[var(--spacing-lg)]">
          {systems.map((system, i) => {
          const ref = i === 0 ? card1Ref : i === 1 ? card2Ref : card3Ref;
          const isVisible = i === 0 ? card1Visible : i === 1 ? card2Visible : card3Visible;
          const Icon = system.icon;
          return <div key={i} data-index={i} ref={ref} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{
            transitionDelay: `${i * 100}ms`
          }}>
                <Card className="h-full flex flex-col border border-border shadow-soft hover:shadow-medium hover:-translate-y-1 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)]">
                  <CardHeader className="pb-[var(--spacing-md)]">
                    <div className="flex items-center justify-center w-14 h-14 rounded-[var(--radius-sm)] bg-primary/10 mb-[var(--spacing-md)]">
                      <Icon className="size-7 text-primary" strokeWidth={1.5} />
                    </div>
                    <CardTitle className="text-foreground text-xl">
                      {system.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col">
                    <CardDescription className="text-muted-foreground mb-[var(--spacing-md)] flex-1">
                      {system.description}
                    </CardDescription>
                    <Link to={system.linkUrl} className="text-accent font-semibold hover:text-accent-hover transition-colors duration-[var(--duration-fast)] inline-flex items-center gap-1.5 focus-ring">
                      {system.linkText}
                      <span aria-hidden="true">→</span>
                    </Link>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}